import { z } from 'zod';
import { EmailProviderSchema } from './auth.js';
import { createAggregateQuerySchema, createAggregateResponseSchema } from './aggregate.js';
import { BooleanQueryParamSchema, JsonObjectSchema, PaginationQuerySchema, createPaginatedResponseSchema } from './common.js';
import { ProposalActionInputSchema } from './proposal-action.js';
import { SupplierInvoiceStatusSchema } from './supplier-invoice.js';
// =============================================================================
// Email Inbox
// =============================================================================
export const EmailInboxStatusSchema = z.enum(['active', 'disconnected', 'needs_reauth']);
export const EmailInboxSchema = z.object({
    id: z.string(),
    emailAddress: z.string(),
    provider: EmailProviderSchema,
    status: EmailInboxStatusSchema,
    connectedAt: z.string(),
    /** IMAP-specific fields. Only present when provider === 'imap'. */
    imapHost: z.string().nullable().optional(),
    imapPort: z.number().nullable().optional(),
    /** TLS mode for the IMAP connection. One of 'ssl_tls' | 'starttls' | 'none'. */
    imapTlsMode: z.enum(['ssl_tls', 'starttls', 'none']).nullable().optional(),
    imapUsername: z.string().nullable().optional(),
});
export const EmailInboxDetailSchema = EmailInboxSchema.extend({
    connectedBy: z.object({
        id: z.string(),
        name: z.string(),
        email: z.string(),
    }),
});
export const EmailInboxListResponseSchema = createPaginatedResponseSchema(EmailInboxSchema);
export const ConnectEmailInboxAuthorizeResponseSchema = z.object({
    authorizationUrl: z.url(),
});
export const ConnectEmailInboxCallbackBodySchema = z.object({
    code: z.string(),
    state: z.string(),
    /** Number of days of historical emails to fetch on initial sync. null = system default (10 emails). 0 = skip. Capped at 30 days (1 month). */
    initialSyncDays: z.int().min(0).max(30).nullable().optional(),
});
export const DisconnectEmailInboxResponseSchema = z.object({
    success: z.boolean(),
});
/**
 * TLS mode for IMAP connections.
 * - `ssl_tls`: direct TLS on connection (port 993 recommended)
 * - `starttls`: plain connection upgraded via STARTTLS (port 143 recommended)
 * - `none`: no encryption (insecure; for legacy/dev environments only)
 */
export const ImapTlsModeSchema = z.enum(['ssl_tls', 'starttls', 'none']);
/**
 * Which provider folder an `EmailInboxFolder` row, a stored email's copy, or
 * a folder cursor represents, by role rather than by server-side name. The
 * server's actual mailbox/label/folder name (e.g. "Sent Items", "INBOX.Sent",
 * a Gmail user label) is resolved separately per account and stored on the
 * folder row itself (`name`, `providerFolderId`); this is the stable,
 * product-facing identifier shared across Gmail, Microsoft Graph, and IMAP.
 *
 * Replaces the narrower `ImapFolderRoleSchema` (`'INBOX' | 'SENT'`), which
 * only ever needed to name the two mailboxes IMAP synced before the folder
 * model widened sync to every non-excluded folder.
 */
export const EmailFolderRoleSchema = z.enum(['INBOX', 'SENT', 'ARCHIVE', 'DRAFTS', 'JUNK', 'TRASH', 'USER']);
/**
 * Folder roles that are ever synced (linked to stored emails, discovered by
 * `syncInboxFolders`, and swept for updates). Junk, Trash, and Drafts are
 * deliberately excluded: their contents are never ingested as stored emails,
 * so a folder row for them would never gain a link. `ALL` (Gmail's "All Mail")
 * is excluded too - it duplicates every other label and syncing it would
 * massively duplicate ingestion.
 */
export const SYNCED_FOLDER_ROLES = ['INBOX', 'SENT', 'ARCHIVE', 'USER'];
/**
 * A minimal reference to a synced folder, carried on a `StoredEmailMessage`
 * (`folders`) and returned by the folder-list endpoint. Folder ids only -
 * never names - travel through events, per the PII-free payload rule (a
 * folder name can be a matter reference).
 */
export const EmailFolderRefSchema = z.object({
    id: z.string(),
    name: z.string(),
    role: EmailFolderRoleSchema,
});
/** An `EmailInboxFolder` row as returned by the folder-list endpoint. */
export const EmailInboxFolderSchema = EmailFolderRefSchema.extend({
    emailInboxId: z.string(),
});
/**
 * Query for `GET /accounts/:accountId/email-inbox-folders`. `emailInboxId` narrows
 * to one inbox's folders; omitted, the endpoint returns every folder across the
 * inboxes the caller can see (same visibility rule as `/stored-emails`).
 */
export const EmailInboxFoldersQuerySchema = z.object({
    emailInboxId: z.uuid().optional(),
}).extend(PaginationQuerySchema.shape);
export const EmailInboxFoldersResponseSchema = createPaginatedResponseSchema(EmailInboxFolderSchema);
/**
 * Structured error codes returned in the `context.imapErrorCode` field when
 * `POST .../email-inboxes/connect/imap` fails with a 400.
 * The frontend maps these to localised user messages.
 */
export const ImapConnectErrorCodeSchema = z.enum([
    'unreachable',
    'tls_handshake_failed',
    'auth_failed',
    'imap_not_supported',
    'select_inbox_failed',
]);
/**
 * Request body for `POST /accounts/:accountId/email-inboxes/connect/imap`.
 * Connects a generic IMAP inbox to an account. All five credential fields are
 * required. `initialSyncDays` is optional: null means use the system default
 * (fetch 10 recent emails); 0 means skip historical sync.
 */
export const ConnectImapInboxBodySchema = z.object({
    emailAddress: z.email(),
    host: z.string().min(1).max(253),
    port: z.int().min(1).max(65535),
    tlsMode: ImapTlsModeSchema,
    username: z.string().min(1).max(500),
    password: z.string().min(1).max(1000),
    /** Number of days of historical emails to fetch on initial sync. null = system default (10 emails). 0 = skip. Capped at 30 days (1 month). */
    initialSyncDays: z.int().min(0).max(30).nullable().optional(),
});
export const EmailAddressSchema = z.object({
    name: z.string().nullable(),
    email: z.string(),
});
export const EmailMessageSchema = z.object({
    id: z.string(),
    threadId: z.string().nullable(),
    subject: z.string(),
    from: EmailAddressSchema,
    to: z.array(EmailAddressSchema),
    cc: z.array(EmailAddressSchema),
    date: z.string(),
    snippet: z.string(),
    body: z.string(),
    isRead: z.boolean(),
    labels: z.array(z.string()),
});
export const FetchEmailsQuerySchema = z.object({
    emailAddress: z.email(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
});
export const FetchEmailsResponseSchema = z.array(EmailMessageSchema);
export const EmailSortBySchema = z.enum(['date', 'unread']);
export const EmailSortDirectionSchema = z.enum(['asc', 'desc']);
// Date range filter boundary for email list queries.
//
// Accepts a full ISO 8601 UTC datetime (Z suffix), e.g. 2026-04-09T14:30:00Z.
// The API and DB deal exclusively in UTC; any calendar-day to UTC conversion
// (e.g. "the 9th of April in SAST") is the caller's responsibility.
const EmailDateFilterSchema = z.iso.datetime();
/**
 * Filter stored emails by their ignored (non-billable) state.
 * - `'ignored'`: only emails with `ignoredAt` set (non-null)
 * - `'not-ignored'`: only emails with `ignoredAt` null
 * - absent: both states returned (current behaviour, no filtering)
 */
export const EmailIgnoredStateSchema = z.enum(['ignored', 'not-ignored']);
/**
 * Filter stored emails by provider-deletion state (`StoredEmail.providerDeletedAt`).
 * - `'deleted'`: only rows the update funnel has marked provider-deleted
 * - `'not-deleted'`: only rows with `providerDeletedAt` null (includes legacy
 *   rows that predate the update funnel, which are also never deleted)
 * - absent: both states returned (current behaviour, no filtering)
 */
export const EmailDeletedStateSchema = z.enum(['deleted', 'not-deleted']);
/**
 * Filter stored emails by the state of their live proposal (an offered,
 * unconfirmed allocation). See `StoredEmailsQuerySchema.proposalState`'s own
 * comment for the exact predicate each value narrows to.
 */
export const EmailProposalStateSchema = z.enum(['pending', 'dismissed']);
/**
 * The filters an account-wide stored-email list shares with its totals
 * (`aggregateAllStoredEmails`): everything that decides WHICH emails, and none
 * of the paging or ordering. The list extends this the way the invoice list
 * extends its filter shape, so the two cannot drift on what "the same filters"
 * means.
 */
export const StoredEmailsFiltersQuerySchema = z.object({
    // Batch-resolve a known id set in one request (repeated `ids` params, as on
    // the clients list). Used by the activity diary to fetch source-email
    // previews for a page of activities without a per-id request fan-out.
    // Rows the caller cannot see (another member's inbox) are silently omitted,
    // mirroring the endpoint's visibility scoping - absence, not an error.
    ids: z.array(z.uuid()).max(100).optional(),
    direction: z.enum(['sent', 'received']).optional(),
    // Inclusive lower/upper bounds on StoredEmail.date, as UTC ISO datetimes.
    // See EmailDateFilterSchema above. Callers are responsible for converting
    // local calendar days to UTC instants before calling this endpoint.
    dateFrom: EmailDateFilterSchema.optional(),
    dateTo: EmailDateFilterSchema.optional(),
    emailInboxId: z.uuid().optional(),
    clientId: z.uuid().optional(),
    clientProjectId: z.uuid().optional(),
    isRead: BooleanQueryParamSchema.optional(),
    processingStatus: z.enum(['pending', 'processing', 'processed', 'failed']).optional(),
    matchState: z.enum(['matched', 'unmatched']).optional(),
    // Filter by presence of a real (non-inline, above the size floor) attachment.
    // `true` -> only emails with at least one attachment; `false` -> only emails
    // with none; absent -> both (current behaviour). Sent as the string
    // 'true'/'false' per BooleanQueryParamSchema.
    hasAttachment: BooleanQueryParamSchema.optional(),
    ignoredState: EmailIgnoredStateSchema.optional(),
    // Narrow to emails currently linked to one synced folder (see
    // EmailInboxFolderSchema). A row whose folder state is unknown (legacy,
    // `providerStateSyncedAt: null`) never matches, the same way it returns
    // `folders: null` rather than an empty set.
    folderId: z.uuid().optional(),
    deletedState: EmailDeletedStateSchema.optional(),
    /**
     * Filter by the state of an email's live proposal (an offered, unconfirmed
     * allocation). `pending` narrows to emails carrying a live proposal and no
     * chosen match; `dismissed` narrows to emails whose most recent proposal
     * was dismissed. Absent returns both states alongside everything else
     * (current behaviour, no filtering).
     */
    proposalState: EmailProposalStateSchema.optional(),
    // Free-text search across sender (name or address), subject, snippet, and
    // body, matched case-insensitively as a substring. `.trim().min(1)` so an
    // empty `?search=` from a cleared input is rejected rather than narrowing
    // the list to nothing (same shape as the bank-statement search filters).
    search: z.string().trim().min(1).max(200).optional(),
});
export const StoredEmailsQuerySchema = StoredEmailsFiltersQuerySchema.extend({
    sortBy: EmailSortBySchema.optional().prefault('date'),
    sortDirection: EmailSortDirectionSchema.optional().prefault('desc'),
}).extend(PaginationQuerySchema.shape);
// =============================================================================
// Stored emails: totals
// =============================================================================
/** Dimensions `aggregateAllStoredEmails` can group by. */
export const StoredEmailsAggregateDimensionSchema = z.enum(['direction']);
// Strict, unlike the list it shares its filters with: a new route must reject unknown keys.
export const StoredEmailsAggregateQuerySchema = createAggregateQuerySchema(StoredEmailsFiltersQuerySchema, StoredEmailsAggregateDimensionSchema).strict();
// Count-only: an email carries no amount, so `count` (added by the envelope) is the only measure.
export const StoredEmailsAggregateResponseSchema = createAggregateResponseSchema({
    measures: z.object({}),
    // 'sent' or 'received'. Typed as a string because the column is one.
    key: z.object({ direction: z.string().optional() }),
    filter: z.object({
        ids: z.array(z.string()).nullable(),
        direction: z.string().nullable(),
        dateFrom: z.string().nullable(),
        dateTo: z.string().nullable(),
        emailInboxId: z.string().nullable(),
        clientId: z.string().nullable(),
        clientProjectId: z.string().nullable(),
        isRead: z.boolean().nullable(),
        processingStatus: z.string().nullable(),
        matchState: z.enum(['matched', 'unmatched']).nullable(),
        hasAttachment: z.boolean().nullable(),
        ignoredState: EmailIgnoredStateSchema.nullable(),
        folderId: z.string().nullable(),
        deletedState: EmailDeletedStateSchema.nullable(),
        proposalState: EmailProposalStateSchema.nullable(),
        search: z.string().nullable(),
    }),
    dimension: StoredEmailsAggregateDimensionSchema,
});
/**
 * Reason why a match (allocated or proposed) did not, or would not, produce a
 * draft activity. Distinct from `CalendarNoActivityReasonSchema` - the value
 * sets do not overlap - so this is its own enum rather than an `.extract()`
 * of the calendar one.
 *
 * - `NO_MATTER`: no `clientProjectId` was attached to the match.
 * - `CONTENT_FILTER`: the matcher flagged the email as non-billable content
 *   (an auto-reply, a calendar invite, etc - see `contentFilterReason`).
 * - `MARKED_NON_BILLABLE`: a human marked the email non-billable
 *   (`ignoredAt`); the allocation itself is untouched.
 */
export const EmailNoActivityReasonSchema = z.enum([
    'NO_MATTER',
    'CONTENT_FILTER',
    'MARKED_NON_BILLABLE',
]);
export const EmailClientMatchSchema = z.object({
    emailClientMatchId: z.string(),
    clientId: z.string(),
    clientName: z.string(),
    matchType: z.string(),
    matchedField: z.string(),
    clientProjectId: z.string().nullable().optional(),
    clientProjectName: z.string().nullable().optional(),
    /**
     * How the matter (clientProject) was matched, or null when no matter was
     * attached. One of `reference_match`, `name_match`, `fuzzy_match`. Distinct
     * from `matchedField`, which carries the dominant (usually client) signal -
     * this is the matter-specific reason the UI uses to explain the suggestion.
     * Kept loosely typed as a string to match `matchedField` / `matchType`.
     */
    projectMatchMethod: z.string().nullable().optional(),
    projectConfidence: z.number().nullable().optional(),
    /**
     * Structural non-billable signal the matcher detected for this candidate,
     * or null - a `ContentFilterReason`, whose current set lives in
     * `domains/email-processor/content-filter.ts`. Left as a plain string on the
     * wire deliberately: the backend adds reasons as it learns to detect more
     * kinds of machine mail, and a client that has not shipped yet must render
     * an unknown one rather than fail to parse the response. Drives
     * `shouldCreateActivity` to false when set. The raw `projectConfidence`
     * stays unmodified so the UI can show the real belief.
     */
    contentFilterReason: z.string().nullable().optional(),
    /**
     * When this match was dismissed as a proposal, or null/absent when it has
     * never been dismissed. A dismissed match is soft-hidden, never deleted:
     * the row stays as a permanent record that a human rejected this
     * candidate. Optional on the wire so a Bill bundle deployed ahead of the
     * backend that produces it keeps parsing the response.
     */
    dismissedAt: z.iso.datetime().nullable().optional(),
    /**
     * When a human confirmed this match as the email's allocation, or
     * null/absent when it has not been confirmed. Write-once: unallocating
     * the email later does not clear it, so it remains a durable record of
     * who confirmed what and when. Optional on the wire for the same
     * ahead-of-backend reason as `dismissedAt`.
     */
    confirmedAt: z.iso.datetime().nullable().optional(),
    /**
     * Policy decision recorded at match-write time. true when the backend
     * reconciler will (or did) create a draft activity for this match. false
     * when the match is purely associative (low confidence or content-filter
     * reason). A manual match sets it true only when a matter is chosen AND the
     * account's auto-drafting is on; with auto-drafting off it stays false, so
     * the pill never promises a draft the reconciler will not create.
     */
    shouldCreateActivity: z.boolean(),
    /**
     * Why no activity was auto-created for this match (allocated or
     * proposed), or null when a live activity exists (`activityStatus`
     * non-null). See `EmailNoActivityReasonSchema` for the full vocabulary
     * and the precedence between its cases.
     */
    noActivityReason: EmailNoActivityReasonSchema.nullable(),
    /**
     * Status of the activity sourced from this match, or null when no activity
     * row exists (policy said no, or eventual-consistency lag). The row pill
     * and detail-modal alert both branch on this plus `activityArchived`.
     */
    activityStatus: z.enum(['DRAFT', 'APPROVED', 'INVOICED']).nullable().optional(),
    /**
     * true when the sourced activity has been soft-deleted. The association
     * (chosenMatchId) survives archiving; the UI surfaces the deleted state
     * so the user can optionally remove the allocation too.
     */
    activityArchived: z.boolean().optional(),
    /**
     * true when the underlying client has been archived (soft-deleted). The
     * match row keeps the original clientId; the UI should label the state
     * distinctly from an active match so the user knows new activity cannot
     * be added without unarchiving the client.
     */
    clientArchived: z.boolean().optional(),
    /** Analogous to clientArchived for the matter (ClientProject). */
    clientProjectArchived: z.boolean().optional(),
});
/**
 * Live expense linked to a stored email attachment (via SupplierInvoice.sourceEmailAttachmentId).
 * Status is never 'VOIDED' – voided expenses are filtered out by the read path so the
 * attachment self-heals back to the "Create expense" affordance on void.
 * Shared shape used on both the per-attachment endpoint and the email list row pill.
 */
export const AttachmentExpenseSchema = z.object({
    id: z.string(),
    status: SupplierInvoiceStatusSchema,
});
/**
 * Row-level summary of the live expenses an email has produced across all its
 * attachments. The collapsed email row shows the real status pill (like
 * activities) only when there is exactly one; when there are several it shows a
 * neutral "N expenses" chip rather than fabricating a single status for
 * divergent siblings. Detail per attachment lives in the expanded row.
 *
 * - `count` is the number of live (non-voided) expenses across the attachments.
 * - `expense` carries the single live expense when `count === 1`; it is null
 *   when `count >= 2` (the row renders the count chip; the user expands for the
 *   per-attachment pills + preview cards).
 *
 * The field itself is null when the email has no live expense, and self-heals to
 * null as expenses are voided.
 */
export const EmailExpenseSummarySchema = z.object({
    count: z.number().int().min(1),
    expense: AttachmentExpenseSchema.nullable(),
});
export const StoredEmailMessageSchema = EmailMessageSchema.extend({
    storedEmailId: z.string(),
    /** Inbox the email belongs to. The aggregated /stored-emails endpoint
     * returns rows from multiple inboxes; the UI uses this to render the
     * inbox address on each row when more than one inbox is connected. */
    emailInboxId: z.string(),
    direction: z.enum(['sent', 'received']),
    processingStatus: z.enum(['pending', 'processing', 'processed', 'failed']),
    /**
     * The current allocation for this email, or null when unallocated. An
     * email is allocated to at most one (client, project) tuple at a time -
     * the structural invariant on `StoredEmail.chosenMatchId` on the server.
     * The full candidate history is not exposed today; when the candidate-
     * picker UI ships it will come through a separate endpoint.
     */
    chosenMatch: EmailClientMatchSchema.nullable().optional(),
    /**
     * The live, undecided offer to allocate this email, or - when there is no
     * live offer - the most recently dismissed one, so the UI can still
     * explain what was rejected and let the user reopen it. Null/absent only
     * when nothing was ever offered. A proposal is not a separate row - it is
     * the same `EmailClientMatch` shape, read before a human confirmed it (it
     * is then `chosenMatch` instead) or as the last one dismissed
     * (`buildProposalByEmailId` in `email-inbox.service.ts`).
     * Optional on the wire so a Bill bundle deployed ahead of the backend
     * that produces it keeps parsing the response.
     */
    proposal: EmailClientMatchSchema.nullable().optional(),
    /**
     * ISO datetime when the email was marked non-billable, or null when not
     * marked. The UI renders a collapsed row with an "Undo" button when this
     * is non-null. Independent of `chosenMatch`: marking an allocated email
     * non-billable leaves the allocation in place and only stops it from
     * logging time - the two answers (what it is allocated to, and whether
     * that allocation should log time) are recorded separately.
     */
    ignoredAt: z.iso.datetime().nullable(),
    /**
     * Who marked the email non-billable, or null. A non-null value is the
     * user id of the person who manually marked it. A null value (combined
     * with non-null `ignoredAt`) means the email was auto-marked
     * by the per-user sender allowlist learning rule.
     */
    ignoredByUserId: z.string().nullable(),
    /**
     * Whether the standing ignore rule (IgnoredEmailAddress allowlist entry)
     * is currently active for this email's counterparty address(es). Derived
     * live from the allowlist at list-request time; never stored on the row.
     *
     * - received: true when the sender's normalised address is on the per-user
     *   allowlist for the inbox owner.
     * - sent: true when `to` is non-empty and ALL recipients' normalised
     *   addresses are on the allowlist (mirrors the processor's auto-ignore
     *   predicate exactly).
     * - false when no rule exists, `to` is empty (sent), or any recipient is
     *   not on the list (partial).
     *
     * The UI uses this to choose the correct "undo" affordance: when true,
     * "Un-ignore this one" + "Stop ignoring sender/recipient(s)"; when false,
     * plain "Undo".
     */
    ignoreRuleActive: z.boolean(),
    /**
     * Summary of the live expenses this email has produced across its
     * attachments (status pill when exactly one, "N expenses" count chip when
     * several), or null when none. Only populated by the account-scoped list
     * endpoint (`GET /accounts/:accountId/stored-emails`); the per-inbox list
     * always returns null. Self-heals to null as sourced expenses are voided.
     */
    expenseSummary: EmailExpenseSummarySchema.nullable(),
    /**
     * True when the email carries at least one real attachment (a row in
     * `email_attachments`, i.e. non-inline and above the size floor - inline
     * parts are already dropped at ingest). Derived live at list-request time
     * from the attachments relation; never stored on the row. Both list
     * endpoints (per-inbox and account-scoped) populate it truthfully. The UI
     * shows a paperclip on the collapsed row and drives the "has attachment"
     * filter from it.
     */
    hasAttachment: z.boolean(),
    /**
     * The synced folders this email is currently linked to, or `null` when its
     * folder state has never been observed by the update funnel (a legacy row
     * predating it - `StoredEmail.providerStateSyncedAt` is null). A row the
     * funnel HAS observed but that carries no links (e.g. a Gmail message
     * archived out of every label) returns `[]`, distinct from the legacy
     * "unknown" case.
     */
    folders: z.array(EmailFolderRefSchema).nullable(),
    /**
     * When this email was marked provider-deleted (mailbox-wide, or its last
     * folder link removed), or null when not deleted. Deleted rows stay
     * visible in the list with this marker rather than disappearing.
     */
    providerDeletedAt: z.iso.datetime().nullable(),
});
export const StoredEmailsResponseSchema = createPaginatedResponseSchema(StoredEmailMessageSchema);
export const CreateManualEmailMatchInputSchema = z.object({
    clientId: z.uuid('clientId must be a valid UUID'),
    clientProjectId: z.uuid('clientProjectId must be a valid UUID').optional(),
    /**
     * The person's answer to "is there billable time here", when they gave
     * one. Same contract as `ConfirmProposalInputSchema.billable`: omitted
     * drafts exactly as a manual allocation always has, `false` records the
     * email as not billable while keeping the allocation, and `true` drafts
     * even where the firm's auto-draft setting would withhold it (refused
     * without a matter).
     */
    billable: z.boolean().optional(),
});
export const ManualEmailMatchResponseSchema = EmailClientMatchSchema;
export const DeleteManualEmailMatchParamsSchema = z.object({
    accountId: z.uuid(),
    storedEmailId: z.uuid(),
    matchId: z.uuid(),
});
export const DeleteManualEmailMatchResponseSchema = z.object({
    success: z.boolean(),
});
export const PromoteEmailMatchParamsSchema = z.object({
    accountId: z.uuid(),
    storedEmailId: z.uuid(),
    matchId: z.uuid(),
});
export const PromoteEmailMatchResponseSchema = z.object({
    success: z.boolean(),
});
/**
 * Request body for `POST .../proposal/confirm`: the match being confirmed,
 * plus the person's answer to the second question the row asks - is there
 * billable time here. Omitted means "as predicted": the draft follows
 * `proposal.noActivityReason` exactly as a plain confirm always has. `false`
 * records the email as not billable (the allocation is still kept); `true`
 * drafts time even where the prediction withheld it, and is refused when the
 * proposal names no matter. Dismiss takes no such answer, so it keeps the
 * plain `ProposalActionInputSchema` (calendar's dismiss and confirm both do,
 * since calendar has no billable question).
 */
export const ConfirmProposalInputSchema = ProposalActionInputSchema.extend({
    billable: z.boolean().optional(),
});
// Path params for the two mark-non-billable routes (POST and DELETE share
// the same shape). The matchId is intentionally absent: marking is keyed on
// the stored email itself, not a candidate.
export const IgnoreEmailParamsSchema = z.object({
    accountId: z.uuid(),
    storedEmailId: z.uuid(),
});
export const IgnoreEmailResponseSchema = z.object({
    success: z.boolean(),
});
/**
 * Optional querystring for POST .../mark-non-billable.
 * `train` controls whether the mark also upserts the counterparty address
 * into the per-user IgnoredEmailAddress allowlist. Absent = true (today's
 * behaviour: mark trains).
 */
export const IgnoreEmailQuerySchema = z.object({
    train: BooleanQueryParamSchema.optional(),
});
/**
 * Optional querystring for DELETE .../mark-non-billable.
 * `untrain` controls whether the undo also deletes the counterparty addresses
 * from the per-user IgnoredEmailAddress allowlist. Absent = true (today's
 * behaviour: undo untrains).
 */
export const UnignoreEmailQuerySchema = z.object({
    untrain: BooleanQueryParamSchema.optional(),
});
/**
 * Request body for PUT .../ignore-rule.
 * `active: true` upserts the per-user allowlist entry for the email's
 * counterparty; `active: false` removes it. Never touches `ignoredAt`.
 */
export const SetEmailIgnoreRuleInputSchema = z.object({
    active: z.boolean(),
});
export const StoredEmailDetailSchema = z.object({
    id: z.string(),
    subject: z.string(),
    snippet: z.string(),
    date: z.string(),
    direction: z.enum(['sent', 'received']),
    from: EmailAddressSchema,
    to: z.array(EmailAddressSchema),
});
// =============================================================================
// Email Attachments
// =============================================================================
// AttachmentExpenseSchema and AttachmentExpense are defined above (before
// StoredEmailMessageSchema which also references them). Reused here on the
// attachment endpoint response.
/**
 * Lightweight attachment metadata returned by the lazy-load attachment endpoint.
 * `filename` is the decrypted display name (never the raw KMS blob).
 * `expense` carries the live expense sourced from this attachment, or null when
 * no email-create expense exists (or after the expense is voided).
 */
export const StoredEmailAttachmentSchema = z.object({
    id: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int(),
    expense: AttachmentExpenseSchema.nullable(),
});
export const StoredEmailAttachmentsResponseSchema = z.object({
    attachments: z.array(StoredEmailAttachmentSchema),
});
// =============================================================================
// Admin - Email Match Quality Inspector
// =============================================================================
// The cursor for this endpoint is a base64-encoded {date, id} keyset token
// (not a UUID) because pagination is by (date DESC, id DESC) rather than by
// a single entity ID. The PaginationQuerySchema cursor constraint (uuid) is
// overridden here to accept any non-empty string.
export const SuperAdminMatchQualityQuerySchema = PaginationQuerySchema.extend({
    from: z.iso.datetime({ error: 'from must be a valid ISO datetime string' }),
    to: z.iso.datetime({ error: 'to must be a valid ISO datetime string' }),
    accountId: z.uuid().optional(),
    cursor: z.string().min(1).optional(),
});
export const SuperAdminMisclassificationSummarySchema = z.object({
    storedEmailId: z.string(),
    accountId: z.string(),
    accountName: z.string(),
    date: z.string(), // ISO-8601
    fpFnTag: z.enum(['FP', 'FN']),
    chosenClientId: z.string().nullable(),
    chosenClientName: z.string().nullable(),
    chosenProjectId: z.string().nullable(),
    chosenProjectName: z.string().nullable(),
});
export const SuperAdminMisclassificationListResponseSchema = createPaginatedResponseSchema(SuperAdminMisclassificationSummarySchema);
export const SuperAdminMisclassificationDetailParamsSchema = z.object({
    storedEmailId: z.uuid(),
});
export const CandidateMatchSchema = z.object({
    emailClientMatchId: z.string(),
    clientId: z.string(),
    clientName: z.string(),
    matchType: z.string(),
    matchedField: z.string(),
    // How strongly the matched signal identifies this client (0-1). An exact
    // from/to address is 1.0; a cc or a client-name substring is dampened (and
    // further divided when the address maps to several clients). This is the
    // value that ranks an address match above a client-name match - distinct
    // from projectConfidence, which only scores the project once the client is
    // fixed.
    identityConfidence: z.number(),
    clientProjectId: z.string().nullable(),
    clientProjectName: z.string().nullable(),
    // How the project was resolved: "reference_match" | "name_match" |
    // "fuzzy_match" | "single_project", or null when no project was attached.
    projectMatchMethod: z.string().nullable(),
    projectConfidence: z.number().nullable(),
    // Number of active projects the matched client had when the email was
    // processed (approximated by current state). 1 means the single-project
    // auto-attach condition was met; >1 means the project was ambiguous from
    // the client alone.
    activeProjectCount: z.number(),
    contentFilterReason: z.string().nullable(),
    shouldCreateActivity: z.boolean(),
    // Whether a draft activity was created from this candidate, and its current
    // lifecycle. "none" = no draft; "live" = draft exists and is not archived;
    // "archived" = a draft was created then archived (e.g. the allocation was
    // cleared). Lets the inspector distinguish a genuine missed draft from one
    // that was created and later removed.
    activityState: z.enum(['none', 'live', 'archived']),
    isChosen: z.boolean(),
});
export const SuperAdminMisclassificationDetailSchema = z.object({
    storedEmailId: z.string(),
    accountId: z.string(),
    date: z.string(), // ISO-8601
    fpFnTag: z.enum(['FP', 'FN']).nullable(),
    subject: z.string(),
    snippet: z.string(),
    from: EmailAddressSchema,
    to: z.array(EmailAddressSchema),
    cc: z.array(EmailAddressSchema),
    candidates: z.array(CandidateMatchSchema),
    chosenMatch: CandidateMatchSchema.nullable(),
    // Threading + provider metadata. Useful for offline analysis pipelines that
    // want to correlate misclassifications across emails in the same thread or
    // detect mailing-list / auto-reply patterns. None of these fields are
    // encrypted on StoredEmail.
    threadId: z.string().nullable(),
    internetMessageId: z.string().nullable(),
    providerEmailId: z.string(),
    direction: z.enum(['received', 'sent']),
    wordCount: z.number().nullable(),
    labels: z.array(z.string()),
    isRead: z.boolean(),
    autoSubmitted: z.string().nullable(),
    precedence: z.string().nullable(),
    listId: z.string().nullable(),
    hasListUnsubscribe: z.boolean(),
});
export const SuperAdminMisclassificationBodySchema = z.object({
    storedEmailId: z.string(),
    body: z.string(),
});
// =============================================================================
// Sent Email (Transactional)
// =============================================================================
export const SentEmailStatusSchema = z.enum(['PENDING', 'SENT', 'DELIVERED', 'BOUNCED', 'FAILED', 'COMPLAINED']);
export const SentEmailSchema = z.object({
    id: z.string(),
    accountId: z.string().nullable(),
    category: z.string().nullable(),
    subject: z.string(),
    provider: z.string(),
    status: SentEmailStatusSchema,
    sentAt: z.string().nullable(),
    lastEventType: z.string().nullable(),
    lastEventAt: z.string().nullable(),
    failureReason: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const SentEmailListResponseSchema = createPaginatedResponseSchema(SentEmailSchema);
export const ListSentEmailsQuerySchema = z.object({
    status: SentEmailStatusSchema.optional(),
    category: z.string().optional(),
}).extend(PaginationQuerySchema.shape);
export const SendTransactionalEmailInputSchema = z.object({
    to: z.union([z.string(), z.array(z.string())]),
    subject: z.string(),
    html: z.string(),
    text: z.string().optional(),
    cc: z.array(z.string()).optional(),
    bcc: z.array(z.string()).optional(),
    category: z.string().optional(),
    metadata: JsonObjectSchema.optional(),
});
